/**
 * Agent-Live-UI — reine, DOM-freie Logik (testbar):
 *  - Frame-Typen (Spiegel der Server-`live.ts`-Frames; KEIN Server-Import).
 *  - `formatAgentEventSummary` — eine kurze, menschenlesbare Zeile je Event-Typ.
 *  - Reducer für den SSE-Client-State (backfill/agent_event/heartbeat/status) mit
 *    Dedupe per `event_id` und lokalem Cap.
 *  - Helfer für Host-Metrics-Kurzanzeige + URL-Bau für den EventSource.
 *
 * Der EventSource-/DOM-Teil liegt in `agent-live-client.ts` bzw. der View — hier
 * steckt nur die testbare Logik.
 */

export interface LiveEventFrame {
  server_id: string;
  event_id: string;
  seq: number | null;
  ts: number | null;
  map: string;
  type: string;
  actor_name: string | null;
  actor_profile_id: string | null;
  actor_team: string | null;
  victim_name: string | null;
  victim_profile_id: string | null;
  victim_team: string | null;
  weapon: string | null;
  message: string | null;
  fields: Record<string, string>;
}

export interface LiveHostMetrics {
  cpu_percent: number | null;
  load_avg_1: number | null;
  memory_percent: number | null;
  memory_total_bytes: number | null;
  memory_available_bytes: number | null;
  disk_percent: number | null;
  disk_total_bytes: number | null;
  disk_free_bytes: number | null;
  uptime_seconds: number | null;
  platform_system: string | null;
  platform_release: string | null;
}

export interface LiveHeartbeatFrame {
  server_id: string;
  agent_version: string | null;
  sent_at: number | null;
  hostname: string | null;
  queue_size: number | null;
  last_event_ts: number | null;
  host_metrics: LiveHostMetrics | null;
}

// --------------------------------------------------------------------------- #
// Event-Summary (pure)
// --------------------------------------------------------------------------- #

const UNKNOWN_NAME = "?";

function field(ev: LiveEventFrame, key: string): string | null {
  const v = ev.fields[key];
  return v != null && v !== "" ? v : null;
}

function actor(ev: LiveEventFrame): string {
  return ev.actor_name ?? field(ev, "player_name") ?? UNKNOWN_NAME;
}

function victim(ev: LiveEventFrame): string {
  return ev.victim_name ?? field(ev, "victim_name") ?? UNKNOWN_NAME;
}

function weaponOf(ev: LiveEventFrame): string {
  return ev.weapon ?? field(ev, "display_weapon") ?? field(ev, "weapon") ?? field(ev, "object") ?? UNKNOWN_NAME;
}

function chatMessage(ev: LiveEventFrame): string {
  return ev.message ?? field(ev, "message_clean") ?? field(ev, "message") ?? "";
}

function isServerMessage(ev: LiveEventFrame): boolean {
  return (
    ev.type === "ServerMessage" ||
    field(ev, "is_server_message") === "1" ||
    field(ev, "channel") === "ServerMessage"
  );
}

/**
 * Kurze, brauchbare Zusammenfassung eines Live-Events. Bewusst NICHT perfekt —
 * reicht für den Live-Stream. Unbekannte Typen → "<type> event".
 */
export function formatAgentEventSummary(ev: LiveEventFrame): string {
  switch (ev.type) {
    case "kill":
      return `${actor(ev)} killed ${victim(ev)} with ${weaponOf(ev)}`;
    case "headshot":
      return `${actor(ev)} headshot ${victim(ev)} with ${weaponOf(ev)}`;
    case "chat":
    case "ServerMessage": {
      const msg = chatMessage(ev);
      if (isServerMessage(ev)) return `Server: ${msg}`;
      return `${actor(ev)}: ${msg}`;
    }
    case "capture": {
      const cp = field(ev, "cp") ?? field(ev, "control_point") ?? field(ev, "flag") ?? UNKNOWN_NAME;
      const team = ev.actor_team ?? field(ev, "owner_team") ?? field(ev, "attacking_team") ?? UNKNOWN_NAME;
      return `Control point ${cp} changed owner to team ${team}`;
    }
    case "join": {
      const team = ev.actor_team ?? field(ev, "team") ?? UNKNOWN_NAME;
      return `${actor(ev)} joined team ${team}`;
    }
    case "leave":
      return `${actor(ev)} left`;
    case "death":
      return `${actor(ev)} died`;
    case "spawn":
      return `${actor(ev)} spawned`;
    default:
      return `${ev.type} event`;
  }
}

// --------------------------------------------------------------------------- #
// Host-Metrics-Kurzanzeige (pure)
// --------------------------------------------------------------------------- #

export const DASH = "—";

/** Prozentwert wie `37.5%`, oder `—` wenn null. */
export function formatPercent(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? DASH : `${Math.round(v * 10) / 10}%`;
}

/** Grobe Uptime: `3d 4h`, `5h 12m`, `7m`, oder `—`. */
export function formatUptime(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return DASH;
  const s = Math.floor(seconds);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Lokale Uhrzeit aus Unix-Sekunden, oder `—`. (Locale/TZ-abhängig → nicht exakt testen.) */
export function formatClock(ts: number | null | undefined): string {
  if (ts == null || !Number.isFinite(ts)) return DASH;
  try {
    return new Date(ts * 1000).toLocaleTimeString();
  } catch {
    return DASH;
  }
}

/**
 * Clampt einen Prozentwert auf [0, 100]. null/undefined/NaN → `null` (Aufrufer
 * zeigt dann `—` bzw. eine leere Leiste).
 */
export function clampPercent(v: number | null | undefined): number | null {
  if (v == null || !Number.isFinite(v)) return null;
  if (v < 0) return 0;
  if (v > 100) return 100;
  return v;
}

// --------------------------------------------------------------------------- #
// Host-Status (online/stale/offline) — pure
// --------------------------------------------------------------------------- #

export type HeartbeatStatus = "online" | "stale" | "offline" | "unknown";

/** Schwellen (Sekunden) für die Online-Bewertung aus `sent_at`. */
export const HEARTBEAT_ONLINE_MAX_S = 90;
export const HEARTBEAT_STALE_MAX_S = 180;

/**
 * Online-Status aus dem letzten Heartbeat:
 *  - `online`  : Alter ≤ 90 s
 *  - `stale`   : 90 s < Alter ≤ 180 s
 *  - `offline` : Alter > 180 s
 *  - `unknown` : keine berechenbare Zeit (`sent_at` fehlt/ungültig)
 *
 * `nowMs` ist die Client-Uhr in Millisekunden. Negatives Alter (Uhr-Schieflage)
 * wird als 0 behandelt → `online`.
 */
export function getHeartbeatStatus(hb: LiveHeartbeatFrame | null | undefined, nowMs: number): HeartbeatStatus {
  const sentAt = hb?.sent_at;
  if (sentAt == null || !Number.isFinite(sentAt)) return "unknown";
  const ageS = Math.max(0, Math.floor(nowMs / 1000) - sentAt);
  if (ageS <= HEARTBEAT_ONLINE_MAX_S) return "online";
  if (ageS <= HEARTBEAT_STALE_MAX_S) return "stale";
  return "offline";
}

/** Sortier-Rang: online < stale < unknown < offline (kleiner = weiter oben). */
const STATUS_RANK: Record<HeartbeatStatus, number> = {
  online: 0,
  stale: 1,
  unknown: 2,
  offline: 3,
};

/**
 * Sortiert Heartbeats für die Status-Cards: nach Status-Rang (online zuerst),
 * dann `server_id` alphabetisch. Reine Funktion (kopiert, mutiert die Eingabe nicht).
 */
export function sortHeartbeatsForStatus(
  heartbeats: LiveHeartbeatFrame[],
  nowMs: number,
): LiveHeartbeatFrame[] {
  return [...heartbeats].sort((a, b) => {
    const ra = STATUS_RANK[getHeartbeatStatus(a, nowMs)];
    const rb = STATUS_RANK[getHeartbeatStatus(b, nowMs)];
    if (ra !== rb) return ra - rb;
    return a.server_id.localeCompare(b.server_id);
  });
}

// --------------------------------------------------------------------------- #
// SSE-Client-State + Reducer (pure)
// --------------------------------------------------------------------------- #

export type LiveStatus = "connecting" | "connected" | "error" | "closed";

export interface AgentLiveState {
  status: LiveStatus;
  /** Events, NEUESTE ZUERST. */
  events: LiveEventFrame[];
  /** Letzter Heartbeat je server_id. */
  heartbeats: Record<string, LiveHeartbeatFrame>;
  /** Client-Uhr (ms) des letzten Live-Events, für die "letztes Event"-Anzeige. */
  lastEventAtMs: number | null;
  /** `server_time` aus dem `ready`-Frame (Unix-Sekunden). */
  serverTime: number | null;
  /** Gesamtzahl empfangener (neuer) Events. */
  eventCount: number;
}

/** Lokales Limit gehaltener Events (gegen unbounded Speicher). */
export const MAX_LOCAL_EVENTS = 300;

export function initialAgentLiveState(): AgentLiveState {
  return {
    status: "connecting",
    events: [],
    heartbeats: {},
    lastEventAtMs: null,
    serverTime: null,
    eventCount: 0,
  };
}

export function applyStatus(state: AgentLiveState, status: LiveStatus): AgentLiveState {
  if (state.status === status) return state;
  return { ...state, status };
}

export function applyReady(state: AgentLiveState, data: { server_time?: number } | null): AgentLiveState {
  const serverTime = data && typeof data.server_time === "number" ? data.server_time : state.serverTime;
  return { ...state, status: "connected", serverTime };
}

/**
 * Übernimmt den initialen Backfill (Server liefert ÄLTESTE zuerst). Bereits
 * vorhandene `event_id`s werden übersprungen; Backfill landet UNTER schon
 * angezeigten (neueren) Live-Events, intern neueste-zuerst, dann gecappt.
 */
export function applyBackfill(state: AgentLiveState, events: LiveEventFrame[]): AgentLiveState {
  if (!Array.isArray(events) || events.length === 0) return state;
  const seen = new Set(state.events.map((e) => e.event_id));
  const add = events.filter((e) => e && e.event_id && !seen.has(e.event_id));
  if (add.length === 0) return state;
  const merged = [...state.events, ...add.reverse()].slice(0, MAX_LOCAL_EVENTS);
  return { ...state, events: merged };
}

/**
 * Nimmt ein einzelnes Live-Event auf: Dedupe per `event_id` (Duplikat → State
 * unverändert), sonst oben anfügen (neueste zuerst), cappen, `lastEventAtMs`
 * + Zähler aktualisieren.
 */
export function applyAgentEvent(state: AgentLiveState, ev: LiveEventFrame, nowMs: number): AgentLiveState {
  if (!ev || !ev.event_id) return state;
  if (state.events.some((e) => e.event_id === ev.event_id)) return state;
  const events = [ev, ...state.events].slice(0, MAX_LOCAL_EVENTS);
  return { ...state, events, lastEventAtMs: nowMs, eventCount: state.eventCount + 1 };
}

/** Aktualisiert den letzten Heartbeat je server_id. */
export function applyHeartbeat(state: AgentLiveState, hb: LiveHeartbeatFrame): AgentLiveState {
  if (!hb || !hb.server_id) return state;
  return { ...state, heartbeats: { ...state.heartbeats, [hb.server_id]: hb } };
}

// --------------------------------------------------------------------------- #
// URL-Bau für den EventSource (pure)
// --------------------------------------------------------------------------- #

export interface LiveQueryInput {
  serverId?: string | null;
  limit?: number;
  types?: string[] | null;
}

export const LIVE_ENDPOINT = "/agent/live/events";

/** Baut `/agent/live/events?limit=&server_id=&types=` defensiv. */
export function buildLiveUrl(input: LiveQueryInput = {}): string {
  const params = new URLSearchParams();
  const limit = input.limit != null && Number.isFinite(input.limit) ? Math.trunc(input.limit) : 100;
  params.set("limit", String(Math.min(500, Math.max(1, limit))));
  const sid = (input.serverId ?? "").trim();
  if (sid) params.set("server_id", sid);
  const types = (input.types ?? []).map((t) => t.trim()).filter((t) => t !== "");
  if (types.length) params.set("types", Array.from(new Set(types)).join(","));
  return `${LIVE_ENDPOINT}?${params.toString()}`;
}

/** Auswahl im Type-Filter der UI (leer = alle). */
export const AGENT_LIVE_TYPE_OPTIONS = [
  "kill",
  "headshot",
  "chat",
  "capture",
  "join",
  "death",
  "heartbeat",
] as const;

// --------------------------------------------------------------------------- #
// Killfeed-Korrelation (pure) — kill primär, headshot=Badge, death=Fallback
// --------------------------------------------------------------------------- #
//
// Baut aus den bereits in der UI vorhandenen, normalisierten Live-Events
// (`LiveEventFrame[]`, newest-first) eine kleine Killfeed-Liste. KEIN DB-Polling,
// keine eigene Quelle — rein eine Sicht auf die schon gestreamten Agent-Events.
// Regeln 1:1 wie der Backend-Helfer `src/agent/killfeed.ts`, nur auf den
// flachen UI-Frames mit Convenience-Feldern (actor_*/victim_*/weapon).

/** Standard-Korrelationsfenster headshot↔kill bzw. death↔kill (Sekunden). */
export const KILLFEED_WINDOW_SECONDS = 1.5;

/** Lokales Limit der angezeigten Killfeed-Zeilen. */
export const MAX_KILLFEED_ITEMS = 100;

export type KillfeedKind = "kill" | "headshot" | "death";

export interface KillfeedItem {
  /** `kill` = primär, `headshot` = unkorrelierter Headshot (Debug/sekundär), `death` = Fallback. */
  kind: KillfeedKind;
  event_id: string;
  server_id: string;
  map: string;
  ts: number | null;
  actor_name: string | null;
  actor_profile_id: string | null;
  actor_team: string | null;
  victim_name: string | null;
  victim_profile_id: string | null;
  victim_team: string | null;
  weapon: string | null;
  kill_type: string | null;
  /** True, wenn ein passendes `headshot`-Event diesem kill zugeordnet wurde. */
  headshot: boolean;
  /** `event_id` des korrelierten Headshots, falls vorhanden. */
  headshot_event_id: string | null;
}

export interface BuildKillfeedOptions {
  windowSeconds?: number;
  limit?: number;
  /** Unkorrelierte Headshots als sekundäre Debug-Zeile zeigen (Default an). */
  includeUnmatchedHeadshots?: boolean;
  /** `death` ohne passenden kill als Fallback-Zeile zeigen (Default an). */
  includeUnmatchedDeaths?: boolean;
}

/** Roh-Waffe eines Frames oder `null` (kein "?"-Fallback hier). */
function killfeedWeapon(ev: LiveEventFrame): string | null {
  return ev.weapon ?? field(ev, "display_weapon") ?? field(ev, "weapon") ?? field(ev, "object");
}

function killType(ev: LiveEventFrame): string | null {
  return field(ev, "kill_type");
}

function sameMapIfBoth(a: string, b: string): boolean {
  if (a && b) return a === b;
  return true; // fehlt eine Map → kein Ausschluss
}

function withinWindow(a: number | null, b: number | null, windowSeconds: number): boolean {
  if (a == null || b == null) return false;
  return Math.abs(a - b) <= windowSeconds;
}

function weaponCompatible(a: string | null, b: string | null): boolean {
  if (a && b) return a === b; // nur wenn BEIDE Waffen vorhanden sind, muss sie gleich sein
  return true;
}

/**
 * True, wenn ein `headshot`-Event zum gegebenen `kill`-Event gehört.
 * profile_id-Match wird bevorzugt; fehlen die ids, greift der Name-Fallback.
 */
export function headshotMatchesKill(
  kill: LiveEventFrame,
  headshot: LiveEventFrame,
  windowSeconds: number = KILLFEED_WINDOW_SECONDS,
): boolean {
  if (kill.type !== "kill" || headshot.type !== "headshot") return false;
  if (kill.server_id !== headshot.server_id) return false;
  if (!sameMapIfBoth(kill.map, headshot.map)) return false;
  if (!withinWindow(kill.ts, headshot.ts, windowSeconds)) return false;
  if (!weaponCompatible(killfeedWeapon(kill), killfeedWeapon(headshot))) return false;

  const ka = kill.actor_profile_id;
  const kv = kill.victim_profile_id;
  const ha = headshot.actor_profile_id;
  const hv = headshot.victim_profile_id;
  if (ka && kv && ha && hv) {
    return ka === ha && kv === hv; // profile_id bevorzugt
  }
  // Fallback: Namen müssen beidseitig vorhanden sein und matchen
  const kan = kill.actor_name;
  const kvn = kill.victim_name;
  return !!kan && !!kvn && kan === headshot.actor_name && kvn === headshot.victim_name;
}

/**
 * True, wenn ein `death`-Event durch einen `kill` bereits abgedeckt ist
 * (death.player == kill.victim, gleiches Fenster) → death NICHT separat zeigen.
 */
export function deathCoveredByKill(
  kill: LiveEventFrame,
  death: LiveEventFrame,
  windowSeconds: number = KILLFEED_WINDOW_SECONDS,
): boolean {
  if (kill.type !== "kill" || death.type !== "death") return false;
  if (kill.server_id !== death.server_id) return false;
  if (!sameMapIfBoth(kill.map, death.map)) return false;
  if (!withinWindow(kill.ts, death.ts, windowSeconds)) return false;

  // death.actor_* = der gestorbene Spieler = kill.victim_*
  const kv = kill.victim_profile_id;
  const dp = death.actor_profile_id;
  if (kv && dp) return kv === dp;
  const kvn = kill.victim_name;
  return !!kvn && kvn === death.actor_name;
}

function killToItem(kill: LiveEventFrame, headshot: LiveEventFrame | null): KillfeedItem {
  return {
    kind: "kill",
    event_id: kill.event_id,
    server_id: kill.server_id,
    map: kill.map,
    ts: kill.ts,
    actor_name: kill.actor_name,
    actor_profile_id: kill.actor_profile_id,
    actor_team: kill.actor_team,
    victim_name: kill.victim_name,
    victim_profile_id: kill.victim_profile_id,
    victim_team: kill.victim_team,
    weapon: killfeedWeapon(kill),
    kill_type: killType(kill),
    headshot: headshot != null,
    headshot_event_id: headshot?.event_id ?? null,
  };
}

function headshotToItem(hs: LiveEventFrame): KillfeedItem {
  return {
    kind: "headshot",
    event_id: hs.event_id,
    server_id: hs.server_id,
    map: hs.map,
    ts: hs.ts,
    actor_name: hs.actor_name,
    actor_profile_id: hs.actor_profile_id,
    actor_team: hs.actor_team,
    victim_name: hs.victim_name,
    victim_profile_id: hs.victim_profile_id,
    victim_team: hs.victim_team,
    weapon: killfeedWeapon(hs),
    kill_type: null,
    headshot: true,
    headshot_event_id: hs.event_id,
  };
}

function deathToItem(d: LiveEventFrame): KillfeedItem {
  return {
    kind: "death",
    event_id: d.event_id,
    server_id: d.server_id,
    map: d.map,
    ts: d.ts,
    actor_name: d.actor_name ?? field(d, "player_name"),
    actor_profile_id: d.actor_profile_id,
    actor_team: d.actor_team,
    victim_name: null,
    victim_profile_id: null,
    victim_team: null,
    weapon: killfeedWeapon(d),
    kill_type: null,
    headshot: false,
    headshot_event_id: null,
  };
}

/**
 * Baut die Killfeed-Liste aus den UI-Live-Events.
 *  - `kill` → primärer Eintrag; ein passendes `headshot` setzt das Badge und wird
 *    NICHT als eigene Zeile gezeigt.
 *  - unkorrelierte `headshot` → optionale sekundäre Debug-Zeile.
 *  - `death` ohne passenden kill → Fallback-Zeile; sonst unterdrückt.
 *  - `chat`/ServerMessage werden NIE als Killfeed-Quelle genutzt.
 * Ergebnis: newest-first, auf `limit` (Default 100) gekappt.
 */
export function buildKillfeedItems(
  events: LiveEventFrame[],
  options: BuildKillfeedOptions = {},
): KillfeedItem[] {
  const windowSeconds = options.windowSeconds ?? KILLFEED_WINDOW_SECONDS;
  const limit = options.limit ?? MAX_KILLFEED_ITEMS;
  const includeHeadshots = options.includeUnmatchedHeadshots ?? true;
  const includeDeaths = options.includeUnmatchedDeaths ?? true;

  if (!Array.isArray(events) || events.length === 0) return [];

  // Quell-Reihenfolge merken (Index klein = neuer, da UI-State newest-first).
  const order = new Map<LiveEventFrame, number>();
  events.forEach((e, i) => order.set(e, i));

  const kills = events.filter((e) => e.type === "kill");
  const headshots = events.filter((e) => e.type === "headshot");
  const deaths = events.filter((e) => e.type === "death");

  const usedHeadshots = new Set<string>();
  const items: KillfeedItem[] = [];

  for (const kill of kills) {
    const hs = headshots.find(
      (h) => !usedHeadshots.has(h.event_id) && headshotMatchesKill(kill, h, windowSeconds),
    );
    if (hs) usedHeadshots.add(hs.event_id);
    items.push(killToItem(kill, hs ?? null));
  }

  if (includeHeadshots) {
    for (const hs of headshots) {
      if (usedHeadshots.has(hs.event_id)) continue;
      items.push(headshotToItem(hs));
    }
  }

  if (includeDeaths) {
    for (const d of deaths) {
      const covered = kills.some((k) => deathCoveredByKill(k, d, windowSeconds));
      if (!covered) items.push(deathToItem(d));
    }
  }

  // newest-first: ts absteigend, bei Gleichstand die ursprüngliche (neuere) Position.
  const idxOf = (it: KillfeedItem): number => {
    const src = events.find((e) => e.event_id === it.event_id);
    return src ? (order.get(src) ?? 0) : 0;
  };
  items.sort((a, b) => {
    const ta = a.ts ?? -Infinity;
    const tb = b.ts ?? -Infinity;
    if (ta !== tb) return tb - ta;
    return idxOf(a) - idxOf(b);
  });

  return items.slice(0, Math.max(0, limit));
}

// --------------------------------------------------------------------------- #
// Killfeed-Zeilen-Text (pure) — Badge/Headshot wird in der View separat gezeigt
// --------------------------------------------------------------------------- #

export interface KillfeedLabels {
  unknownWeapon: string;
  suicide: string;
  environment: string;
}

/** Default-Labels (EN-nah); die View reicht i18n-Labels durch. */
export const DEFAULT_KILLFEED_LABELS: KillfeedLabels = {
  unknownWeapon: "unknown weapon",
  suicide: "suicide",
  environment: "environment",
};

function nameOr(n: string | null): string {
  return n != null && n.trim() !== "" ? n : UNKNOWN_NAME;
}

/**
 * Menschenlesbare Killfeed-Zeile (ohne Headshot-Badge — das macht die View).
 * Defensiv: fehlende Namen → "?", fehlende Waffe → Label.
 */
export function formatKillfeedText(item: KillfeedItem, labels: KillfeedLabels = DEFAULT_KILLFEED_LABELS): string {
  const actor = nameOr(item.actor_name);
  const victim = nameOr(item.victim_name);
  const weapon = item.weapon ?? labels.unknownWeapon;

  if (item.kind === "death") {
    return `${actor} died`;
  }
  if (item.kind === "headshot") {
    return `${actor} headshot ${victim} with ${weapon}`;
  }

  // kind === "kill" — kill_type berücksichtigen
  switch (item.kill_type) {
    case "self":
      return `${actor} killed themselves with ${weapon}`;
    case "environment":
      return `${nameOr(item.victim_name ?? item.actor_name)} died by ${item.weapon ?? labels.environment}`;
    case "unknown":
      return `${actor} killed ${victim}`;
    default:
      // kein Angreifer → wie Umgebung behandeln
      if (item.actor_name == null) {
        return `${victim} died by ${item.weapon ?? labels.environment}`;
      }
      return `${actor} killed ${victim} with ${weapon}`;
  }
}
